import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import type { EChartsOption } from "echarts";

export type OverviewLinePoint = {
  t: string;
  runs: number;
  copilots: number;
  failed: number;
};

type Props = {
  data: OverviewLinePoint[];
  isLight?: boolean;
  height?: number;
  runsColor?: string;
  sessionsColor?: string;
};

function fmt(n: number) {
  if (n >= 1000) return `${(n / 1000).toFixed(n >= 10000 ? 0 : 1)}k`;
  return String(n);
}

/** Advanced dual-axis area+line chart (Apache ECharts) */
export default function AdvancedOverviewLineChart({
  data,
  isLight = true,
  height = 300,
  runsColor = "#4F46E5",
  sessionsColor = "#0EA5E9",
}: Props) {
  const option = useMemo<EChartsOption>(() => {
    const labels = data.map((d) => d.t);
    const runs = data.map((d) => d.runs);
    const sessions = data.map((d) => d.copilots);
    const failed = data.map((d) => d.failed);
    const axisMuted = isLight ? "#A3A3A3" : "#71717A";
    const split = isLight ? "rgba(0,0,0,0.06)" : "rgba(255,255,255,0.06)";
    const tipBg = isLight ? "#ffffff" : "#18181b";
    const tipBorder = isLight ? "#e5e5e5" : "#3f3f46";
    const tipText = isLight ? "#171717" : "#f4f4f5";
    const tipMuted = isLight ? "#737373" : "#a1a1aa";

    return {
      animationDuration: 650,
      animationEasing: "cubicOut",
      grid: {
        left: 48,
        right: 48,
        top: 28,
        bottom: 28,
        containLabel: false,
      },
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "cross",
          snap: true,
          crossStyle: {
            color: isLight ? "#A3A3A3" : "#52525B",
            width: 1,
            type: "dashed",
          },
          lineStyle: {
            color: isLight ? "#D4D4D4" : "#3F3F46",
            width: 1.25,
            type: "dashed",
          },
          label: {
            backgroundColor: isLight ? "#525252" : "#3f3f46",
            borderRadius: 4,
            padding: [3, 6],
            fontSize: 10,
          },
        },
        backgroundColor: tipBg,
        borderColor: tipBorder,
        borderWidth: 1,
        padding: [10, 12],
        extraCssText: "border-radius:12px;box-shadow:0 10px 28px rgba(0,0,0,0.12);",
        textStyle: { color: tipText, fontSize: 12 },
        formatter: (params) => {
          const items = Array.isArray(params) ? params : [params];
          const idx = (items[0] as { dataIndex?: number })?.dataIndex ?? 0;
          const label = labels[idx] ?? "";
          const runVal = runs[idx] ?? 0;
          const sessVal = sessions[idx] ?? 0;
          const failVal = failed[idx] ?? 0;
          const success = runVal ? ((runVal - failVal) / runVal) * 100 : 100;
          return `
            <div style="min-width:190px">
              <div style="font-weight:600;font-size:11px;color:${tipMuted};margin-bottom:10px">${label}</div>
              <div style="display:flex;justify-content:space-between;gap:24px;margin-bottom:8px">
                <span style="display:flex;align-items:center;gap:8px;color:${tipMuted}">
                  <span style="width:8px;height:8px;border-radius:999px;background:${runsColor}"></span>
                  Orchestration runs
                </span>
                <span style="font-weight:600;font-variant-numeric:tabular-nums">${runVal.toLocaleString()}</span>
              </div>
              <div style="display:flex;justify-content:space-between;gap:24px;margin-bottom:10px">
                <span style="display:flex;align-items:center;gap:8px;color:${tipMuted}">
                  <span style="width:8px;height:8px;border-radius:999px;background:${sessionsColor}"></span>
                  Copilot sessions
                </span>
                <span style="font-weight:600;font-variant-numeric:tabular-nums">${sessVal.toLocaleString()}</span>
              </div>
              <div style="display:flex;justify-content:space-between;gap:24px;padding-top:8px;border-top:1px solid ${tipBorder}">
                <span style="color:${tipMuted}">Success · Failed</span>
                <span style="font-variant-numeric:tabular-nums">
                  <span style="font-weight:600;color:#059669">${success.toFixed(0)}%</span>
                  <span style="color:${tipMuted}"> · </span>
                  <span style="font-weight:600;color:#F43F5E">${failVal}</span>
                </span>
              </div>
            </div>
          `;
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: labels,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: axisMuted,
          fontSize: 11,
          margin: 14,
        },
      },
      yAxis: [
        {
          type: "value",
          name: "Runs",
          nameTextStyle: {
            color: runsColor,
            fontSize: 10,
            padding: [0, 0, 0, 0],
            align: "left",
          },
          nameGap: 10,
          splitNumber: 4,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: runsColor,
            fontSize: 10,
            opacity: 0.8,
            formatter: (v: number) => fmt(v),
          },
          splitLine: {
            show: true,
            lineStyle: { color: split, type: "dashed" },
          },
        },
        {
          type: "value",
          name: "Sessions",
          nameTextStyle: {
            color: sessionsColor,
            fontSize: 10,
            align: "right",
          },
          nameGap: 10,
          splitNumber: 4,
          axisLine: { show: false },
          axisTick: { show: false },
          axisLabel: {
            color: sessionsColor,
            fontSize: 10,
            opacity: 0.8,
            formatter: (v: number) => fmt(v),
          },
          splitLine: { show: false },
        },
      ],
      series: [
        {
          name: "Orchestration runs",
          type: "line",
          smooth: 0.35,
          symbol: "circle",
          symbolSize: 8,
          showSymbol: true,
          yAxisIndex: 0,
          data: runs,
          lineStyle: {
            width: 3,
            color: runsColor,
            shadowColor: isLight ? "rgba(79,70,229,0.25)" : "rgba(79,70,229,0.45)",
            shadowBlur: 8,
            shadowOffsetY: 3,
          },
          itemStyle: {
            color: runsColor,
            borderColor: isLight ? "#fff" : "#18181b",
            borderWidth: 2,
          },
          emphasis: {
            focus: "series",
            scale: 1.35,
            itemStyle: {
              borderWidth: 3,
              shadowBlur: 10,
              shadowColor: "rgba(79,70,229,0.45)",
            },
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: isLight ? "rgba(79,70,229,0.28)" : "rgba(79,70,229,0.4)" },
                { offset: 0.55, color: isLight ? "rgba(79,70,229,0.08)" : "rgba(79,70,229,0.14)" },
                { offset: 1, color: "rgba(79,70,229,0)" },
              ],
            },
          },
          z: 3,
        },
        {
          name: "Copilot sessions",
          type: "line",
          smooth: 0.35,
          symbol: "circle",
          symbolSize: 7,
          showSymbol: true,
          yAxisIndex: 1,
          data: sessions,
          lineStyle: {
            width: 2.75,
            color: sessionsColor,
            shadowColor: isLight ? "rgba(14,165,233,0.22)" : "rgba(14,165,233,0.4)",
            shadowBlur: 7,
            shadowOffsetY: 2,
          },
          itemStyle: {
            color: sessionsColor,
            borderColor: isLight ? "#fff" : "#18181b",
            borderWidth: 2,
          },
          emphasis: {
            focus: "series",
            scale: 1.3,
            itemStyle: {
              borderWidth: 3,
              shadowBlur: 10,
              shadowColor: "rgba(14,165,233,0.4)",
            },
          },
          areaStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: isLight ? "rgba(14,165,233,0.18)" : "rgba(14,165,233,0.3)" },
                { offset: 0.6, color: isLight ? "rgba(14,165,233,0.04)" : "rgba(14,165,233,0.1)" },
                { offset: 1, color: "rgba(14,165,233,0)" },
              ],
            },
          },
          z: 2,
        },
      ],
    };
  }, [data, isLight, runsColor, sessionsColor]);

  return (
    <ReactECharts
      option={option}
      style={{ height, width: "100%" }}
      opts={{ renderer: "canvas" }}
      notMerge
      lazyUpdate
    />
  );
}
